import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Server,
  Plus,
  Trash2,
  Edit3,
  Activity,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Loader2,
  Eye,
  Database,
  ChevronRight,
  Sparkles,
  X,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";

const TRANSPORT_OPTIONS = [
  { value: "stdio", label: "stdio (subprocess)" },
  { value: "sse", label: "sse (HTTP)" },
];

function ServerForm({ initial, onSubmit, onCancel, busy }) {
  const [form, setForm] = useState({
    slug: initial?.slug || "",
    name: initial?.name || "",
    description: initial?.description || "",
    transport: initial?.transport || "stdio",
    command_text: (initial?.command || []).join(" "),
    env_text: Object.entries(initial?.env || {})
      .map(([k, v]) => `${k}=${v}`)
      .join("\n"),
    url: initial?.url || "",
    headers_text: Object.entries(initial?.headers || {})
      .map(([k, v]) => `${k}: ${v}`)
      .join("\n"),
    enabled: initial?.enabled ?? true,
  });

  const handle = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target?.value !== undefined ? e.target.value : e }));

  const submit = () => {
    const payload = {
      slug: form.slug.trim(),
      name: form.name.trim(),
      description: form.description.trim(),
      transport: form.transport,
      enabled: form.enabled,
    };
    if (form.transport === "stdio") {
      payload.command = form.command_text.trim().split(/\s+/).filter(Boolean);
      payload.env = {};
      form.env_text
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean)
        .forEach((line) => {
          const idx = line.indexOf("=");
          if (idx > 0) payload.env[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
        });
    } else {
      payload.url = form.url.trim();
      payload.headers = {};
      form.headers_text
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean)
        .forEach((line) => {
          const idx = line.indexOf(":");
          if (idx > 0) payload.headers[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
        });
    }
    onSubmit(payload);
  };

  return (
    <Card data-testid="mcp-server-form">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{initial ? "Edit Server" : "New MCP Server"}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Slug *</Label>
            <Input value={form.slug} onChange={handle("slug")} placeholder="filesystem"
                   data-testid="mcp-form-slug" />
          </div>
          <div>
            <Label className="text-xs">Name *</Label>
            <Input value={form.name} onChange={handle("name")} placeholder="Filesystem"
                   data-testid="mcp-form-name" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Description</Label>
          <Input value={form.description} onChange={handle("description")}
                 placeholder="Read/write files in the agent workspace"
                 data-testid="mcp-form-description" />
        </div>
        <div>
          <Label className="text-xs">Transport *</Label>
          <select
            value={form.transport}
            onChange={handle("transport")}
            className="w-full mt-1 h-9 px-3 rounded-md border border-input bg-background text-sm"
            data-testid="mcp-form-transport"
          >
            {TRANSPORT_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        {form.transport === "stdio" ? (
          <>
            <div>
              <Label className="text-xs">Command (space-separated) *</Label>
              <Input value={form.command_text} onChange={handle("command_text")}
                     placeholder="npx -y @modelcontextprotocol/server-filesystem /tmp"
                     className="font-mono text-xs"
                     data-testid="mcp-form-command" />
            </div>
            <div>
              <Label className="text-xs">Env (one KEY=VALUE per line)</Label>
              <Textarea value={form.env_text} onChange={handle("env_text")}
                        placeholder={"GITHUB_PERSONAL_ACCESS_TOKEN=ghp_xxx"}
                        className="min-h-[60px] font-mono text-xs"
                        data-testid="mcp-form-env" />
            </div>
          </>
        ) : (
          <>
            <div>
              <Label className="text-xs">URL *</Label>
              <Input value={form.url} onChange={handle("url")}
                     placeholder="https://mcp.example.com/sse"
                     data-testid="mcp-form-url" />
            </div>
            <div>
              <Label className="text-xs">Headers (one Key: Value per line)</Label>
              <Textarea value={form.headers_text} onChange={handle("headers_text")}
                        placeholder={"Authorization: Bearer xxx"}
                        className="min-h-[60px] font-mono text-xs"
                        data-testid="mcp-form-headers" />
            </div>
          </>
        )}
        <div className="flex items-center gap-2">
          <Switch checked={form.enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
                  data-testid="mcp-form-enabled" />
          <Label className="text-xs">Enabled</Label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="mcp-form-cancel">
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy || !form.slug || !form.name} data-testid="mcp-form-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (initial ? "Save" : "Create")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function McpServersPage({ api }) {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // null | "new" | server obj
  const [busy, setBusy] = useState(false);
  const [tools, setTools] = useState({});  // server_id -> tools
  const [testingId, setTestingId] = useState(null);

  const fetchServers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${api}/v1/mcp/servers`);
      setServers(res.data.items || []);
    } catch (err) {
      toast.error("Failed to load servers");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchServers();
  }, [fetchServers]);

  const handleSubmit = async (payload) => {
    setBusy(true);
    try {
      if (editing === "new") {
        await axios.post(`${api}/v1/mcp/servers`, payload);
        toast.success("Server created");
      } else {
        await axios.put(`${api}/v1/mcp/servers/${editing.id}`, payload);
        toast.success("Server updated");
      }
      setEditing(null);
      await fetchServers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Delete '${s.name}'?`)) return;
    try {
      await axios.delete(`${api}/v1/mcp/servers/${s.id}`);
      toast.success("Deleted");
      await fetchServers();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const handleTest = async (s) => {
    setTestingId(s.id);
    try {
      const res = await axios.post(`${api}/v1/mcp/servers/${s.id}/test`);
      if (res.data.ok) {
        setTools((t) => ({ ...t, [s.id]: res.data.tools || [] }));
        toast.success(`Connected · ${res.data.tools?.length || 0} tools`);
      } else {
        toast.error(`Test failed: ${res.data.error || "unknown"}`);
      }
    } catch (err) {
      toast.error("Test failed");
    } finally {
      setTestingId(null);
    }
  };

  const handleSeed = async () => {
    try {
      const res = await axios.post(`${api}/v1/mcp/servers/seed`);
      toast.success(`Seeded · ${res.data.inserted} added, ${res.data.skipped} skipped`);
      await fetchServers();
    } catch (err) {
      toast.error("Seed failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="mcp-servers-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Server className="w-7 h-7 text-emerald-600" />
            MCP Servers
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Model Context Protocol servers exposed to agent jobs. Configure once, attach
            per job.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchServers} data-testid="mcp-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
          <Button variant="outline" onClick={handleSeed} data-testid="mcp-seed-btn">
            <Sparkles className="w-3.5 h-3.5" />
            Seed defaults
          </Button>
          <Button onClick={() => setEditing("new")} data-testid="mcp-new-btn">
            <Plus className="w-3.5 h-3.5" />
            New server
          </Button>
        </div>
      </div>

      {editing && (
        <ServerForm
          initial={editing === "new" ? null : editing}
          onSubmit={handleSubmit}
          onCancel={() => setEditing(null)}
          busy={busy}
        />
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Database className="w-4 h-4" />
            Configured ({servers.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : servers.length === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-500">
              No servers configured. Click "Seed defaults" to bootstrap with filesystem,
              fetch, git, github, brave-search, sqlite, sequential-thinking.
            </div>
          ) : (
            <div className="divide-y">
              {servers.map((s) => (
                <div key={s.id} className="px-4 py-3" data-testid={`mcp-server-${s.slug}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <code className="text-xs font-mono px-2 py-0.5 rounded bg-zinc-100 text-zinc-700">
                          {s.slug}
                        </code>
                        <span className="text-sm font-medium text-zinc-900">{s.name}</span>
                        <Badge variant="outline" className={
                          s.transport === "stdio"
                            ? "border-blue-200 text-blue-700"
                            : "border-emerald-200 text-emerald-700"
                        }>
                          {s.transport}
                        </Badge>
                        {!s.enabled && (
                          <Badge variant="outline" className="border-zinc-300 text-zinc-500">
                            disabled
                          </Badge>
                        )}
                      </div>
                      {s.description && (
                        <p className="text-xs text-zinc-500 mb-1">{s.description}</p>
                      )}
                      <div className="text-xs text-zinc-400 font-mono truncate">
                        {s.transport === "stdio" ? (s.command || []).join(" ") : s.url}
                      </div>
                      {s.env_keys && s.env_keys.length > 0 && (
                        <div className="text-xs text-zinc-400 mt-0.5">
                          env: {s.env_keys.join(", ")}
                        </div>
                      )}
                      {tools[s.id] && (
                        <div className="mt-2 px-2 py-1.5 rounded bg-emerald-50 border border-emerald-200">
                          <div className="text-xs font-medium text-emerald-800 mb-1">
                            {tools[s.id].length} tools available:
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {tools[s.id].slice(0, 12).map((t) => (
                              <code key={t.name} className="text-xs px-1.5 py-0.5 rounded bg-white text-emerald-700 border border-emerald-200">
                                {t.name}
                              </code>
                            ))}
                            {tools[s.id].length > 12 && (
                              <span className="text-xs text-emerald-600">+{tools[s.id].length - 12} more</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button variant="outline" size="sm" disabled={testingId === s.id}
                              onClick={() => handleTest(s)}
                              data-testid={`mcp-test-${s.slug}`}>
                        {testingId === s.id
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <Activity className="w-3.5 h-3.5" />}
                        Test
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setEditing(s)}
                              data-testid={`mcp-edit-${s.slug}`}>
                        <Edit3 className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(s)}
                              data-testid={`mcp-delete-${s.slug}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
